import React, { useEffect, useRef, useState } from 'react';
import { useMicRecorder } from '../voice/useMicRecorder';
import { MicCheck } from '../voice/MicCheck';
import { Mic, Square, Loader2, Keyboard } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { DocTag, TAGS } from './ReviewSendDialog';

const T = {
  post: TAGS.post, pre: TAGS.pre, med: TAGS.med,
  consent: { text: 'Informed consent', cls: 'bg-violet-100 text-violet-800' },
  refusal: { text: 'Informed refusal', cls: 'bg-rose-100 text-rose-800' },
  incomplete: { text: 'Treatment not completed form', cls: 'bg-orange-100 text-orange-800' },
  compliance: { text: 'Compliance form', cls: 'bg-amber-100 text-amber-800' },
  referral: { text: 'Referral letter draft', cls: 'bg-indigo-100 text-indigo-800' },
  chart: { text: 'Notes tab only — never sent', cls: 'bg-slate-200 text-slate-700' },
};

// [what to say, example, documents it creates]
const PROMPTS = [
  ['Patient', 'Name. New patient? Add their cell or email.', []],
  ['Procedure done today', '"extraction #30, bone graft"', [T.post]],
  ['Procedure planned', '"implant #30 in 3 months, crown on 19"', [T.pre, T.consent]],
  ['Treatment declined', '"declined X-rays" / "declined perio treatment"', [T.refusal]],
  ['Treatment not completed', '"never came back to finish the root canal on 14"', [T.incomplete]],
  ['Medication prescribed', '"ibuprofen 600, amoxicillin"', [T.med]],
  ['Health or medication change', '"new diabetes diagnosis", "started Eliquis"', [T.compliance]],
  ['Referral', '"refer to Dr. Smith for endo on 19"', [T.referral]],
  ['Notes', '"anxious, prefers texts, speaks Spanish"', [T.chart]],
];

const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

export const DictationRecorder = ({ onAudio, onText, busy, initialText = '' }) => {
  const [mode, setMode] = useState('voice');
  const [seconds, setSeconds] = useState(0);
  const [text, setText] = useState(initialText);
  const mic = useMicRecorder({ onBlob: onAudio });
  const { recording } = mic;
  const timer = useRef(null);

  useEffect(() => {
    clearInterval(timer.current);
    if (recording) { setSeconds(0); timer.current = setInterval(() => setSeconds((s) => s + 1), 1000); }
    return () => clearInterval(timer.current);
  }, [recording]);

  const start = () => mic.start('record');
  const stop = () => mic.stop();

  return (
    <div className="grid md:grid-cols-5 gap-6" data-testid="dictation-recorder">
      <div className="md:col-span-2 bg-slate-50 border border-slate-200 rounded-xl p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">What to say → what gets sent</p>
        <p className="text-xs text-slate-500 mb-3">Say any of these. Each one creates the document shown, and you review everything before it goes out.</p>
        <ol className="space-y-2.5" data-testid="dictation-guide">
          {PROMPTS.map(([t, d, docs], i) => (
            <li key={t} className="text-sm leading-snug" data-testid={`dictation-guide-${i}`}>
              <span className="font-semibold text-slate-800">{i + 1}. {t}</span>
              <span className="block text-slate-500 text-xs">e.g. {d}</span>
              {docs.length > 0 && <span className="block mt-1">{docs.map((tag) => <DocTag key={tag.text} tag={tag} />)}</span>}
            </li>
          ))}
        </ol>
        <p className="text-[11px] text-slate-400 mt-3">Say "next patient" to add another. Audio is transcribed and then discarded. Only the text is saved.</p>
      </div>

      <div className="md:col-span-3 flex flex-col">
        <div className="flex gap-2 mb-4">
          <Button type="button" size="sm" variant={mode === 'voice' ? 'default' : 'outline'} onClick={() => setMode('voice')} data-testid="dictation-mode-voice">
            <Mic className="h-4 w-4 mr-1" /> Speak
          </Button>
          <Button type="button" size="sm" variant={mode === 'text' ? 'default' : 'outline'} onClick={() => setMode('text')} disabled={recording} data-testid="dictation-mode-text">
            <Keyboard className="h-4 w-4 mr-1" /> Type
          </Button>
        </div>

        {mode === 'voice' ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 border-2 border-dashed border-slate-200 rounded-xl py-10">
            {busy ? (
              <>
                <Loader2 className="h-12 w-12 animate-spin text-sky-600" />
                <p className="text-sm text-slate-600" data-testid="dictation-busy">{busy}</p>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={recording ? stop : start}
                  className={`h-24 w-24 rounded-full flex items-center justify-center text-white shadow-lg transition-transform hover:scale-105 ${recording ? 'bg-rose-600 animate-pulse' : 'bg-sky-600'}`}
                  data-testid={recording ? 'dictation-stop-btn' : 'dictation-start-btn'}
                  aria-label={recording ? 'Stop recording' : 'Start recording'}
                >
                  {recording ? <Square className="h-9 w-9" /> : <Mic className="h-10 w-10" />}
                </button>
                <p className="text-sm font-medium text-slate-700">{recording ? `Recording… ${fmtTime(seconds)} — tap to finish` : 'Tap to start dictating'}</p>
              </>
            )}
            {!busy && <MicCheck mic={mic} prefix="dictation" className="px-4 max-w-md" />}
          </div>
        ) : (
          <div className="flex-1 flex flex-col gap-3">
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={10}
              placeholder='e.g. "Mason Archer, extraction #30 today with bone graft. Implant #30 planned in 3 months. Declined X-rays due to cost. Prescribed amoxicillin 500. New: Eliquis. Very anxious, prefers texts."'
              data-testid="dictation-text-input"
            />
            <Button onClick={() => onText(text)} disabled={!text.trim() || !!busy} data-testid="dictation-process-text-btn">
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{busy}</> : 'Build Visit Card'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DictationRecorder;
